import React, { useState } from 'react';
import { Zap, Users, MapPin, AlertTriangle, CheckCircle, Info, BarChart3, Clock, Trash2 } from 'lucide-react';
import { useCrud } from '../hooks';
import { eventoService } from '../services/api';

const AlocacaoDinamica = () => {
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const [eventoSelecionado, setEventoSelecionado] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingLimpeza, setLoadingLimpeza] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const [mostrarOpcaoLimpeza, setMostrarOpcaoLimpeza] = useState(false);

  const limparAlocacoesExistentes = async () => {
    if (!eventoSelecionado) return;

    setLoadingLimpeza(true);
    setErro('');

    try {
      const response = await fetch(`/api/alocacao/evento/${eventoSelecionado}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setMostrarOpcaoLimpeza(false);
        // Agora executar a alocação dinâmica automaticamente
        await executarAlocacaoDinamica();
      } else {
        setErro(data.message || 'Erro ao limpar alocações existentes');
      }
    } catch (error) {
      setErro('Erro ao limpar alocações: ' + error.message);
    } finally {
      setLoadingLimpeza(false);
    }
  };

  const executarAlocacaoDinamica = async () => {
    if (!eventoSelecionado) {
      setErro('Selecione um evento');
      return;
    }

    setLoading(true);
    setErro('');
    setResultado(null);
    setMostrarOpcaoLimpeza(false);

    try {
      const response = await fetch('/api/alocacao/dinamica', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ evento_id: parseInt(eventoSelecionado) }),
      });

      const data = await response.json();

      if (data.success) {
        setResultado({
          message: data.message,
          alocacoes: data.data || [],
          estatisticas: data.estatisticas
        });
      } else {
        // Verificar se é erro de alocações existentes
        if (data.message.includes('Já existem alocações') || data.message.includes('alocações existentes')) {
          setMostrarOpcaoLimpeza(true);
        }
        setErro(data.message || 'Erro ao executar alocação dinâmica');
      }
    } catch (error) {
      setErro('Erro de comunicação com o servidor: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetar = () => {
    setEventoSelecionado('');
    setResultado(null);
    setErro('');
    setMostrarOpcaoLimpeza(false);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Zap className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="page-title">Alocação Dinâmica</h1>
            <p className="page-subtitle">Distribuição automática e inteligente de caravanas nos quartos</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2">
            <Info className="h-5 w-5" />
            Como Funciona
          </h2>
        </div>
        <div className="card-content">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-blue-600">1</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Análise Inteligente</h3>
                <p className="text-sm text-gray-600">O sistema analisa as caravanas e quartos disponíveis para o evento</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-green-600">2</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Distribuição por Gênero</h3>
                <p className="text-sm text-gray-600">Respeita os gêneros dos quartos (masculino, feminino, misto)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-purple-600">3</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">Otimização</h3>
                <p className="text-sm text-gray-600">Prioriza manter caravanas juntas no mesmo quarto quando possível</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Configuração</h2>
        </div>
        <div className="card-content">
          <div className="form-group">
            <label className="form-label">Selecionar Evento *</label>
            <select
              value={eventoSelecionado}
              onChange={(e) => setEventoSelecionado(e.target.value)}
              className="form-select"
              disabled={loading}
            >
              <option value="">Selecione um evento</option>
              {eventos.map((evento) => (
                <option key={evento.id} value={evento.id}>
                  {evento.nome}
                </option>
              ))}
            </select>
          </div>

          {erro && (
            <div className="space-y-3">
              <div className="alert alert-error">
                <AlertTriangle className="h-4 w-4" />
                <span>{erro}</span>
              </div>
              
              {mostrarOpcaoLimpeza && (
                <div className="alert alert-warning">
                  <Info className="h-4 w-4" />
                  <div className="flex-1">
                    <p className="text-sm mb-2">
                      Para executar a alocação automática, é necessário remover as alocações manuais existentes para este evento.
                    </p>
                    <button
                      onClick={limparAlocacoesExistentes}
                      disabled={loadingLimpeza}
                      className="btn btn-warning btn-sm"
                    >
                      {loadingLimpeza ? (
                        <>
                          <Clock className="h-3 w-3 animate-spin" />
                          Limpando...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-3 w-3" />
                          Limpar e Executar Automaticamente
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={executarAlocacaoDinamica}
              disabled={loading || !eventoSelecionado}
              className="btn btn-primary"
            >
              {loading ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Executar Alocação Automática
                </>
              )}
            </button>
            
            {resultado && (
              <button onClick={resetar} className="btn btn-secondary">
                Nova Distribuição
              </button>
            )}
          </div>
        </div>
      </div>

      {resultado && (
        <div className="space-y-6">
          <div className="alert alert-success">
            <CheckCircle className="h-5 w-5" />
            <span>{resultado.message}</span>
          </div>

          {resultado.estatisticas && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Estatísticas da Distribuição
                </h3>
              </div>
              <div className="card-content">
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="stat-card bg-blue-50 border-blue-200">
                    <div className="stat-icon bg-blue-100">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="stat-value">{resultado.estatisticas.totalCaravanas}</div>
                      <div className="stat-label">Caravanas</div>
                    </div>
                  </div>
                  
                  <div className="stat-card bg-green-50 border-green-200">
                    <div className="stat-icon bg-green-100">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <div className="stat-value">{resultado.estatisticas.totalPessoas}</div>
                      <div className="stat-label">Pessoas</div>
                    </div>
                  </div>

                  <div className="stat-card bg-purple-50 border-purple-200">
                    <div className="stat-icon bg-purple-100">
                      <MapPin className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="stat-value">{resultado.estatisticas.quartosUtilizados}</div>
                      <div className="stat-label">Quartos Usados</div>
                    </div>
                  </div>

                  <div className="stat-card bg-orange-50 border-orange-200">
                    <div className="stat-icon bg-orange-100">
                      <BarChart3 className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="stat-value">{resultado.estatisticas.eficiencia}</div>
                      <div className="stat-label">Pessoas/Alocação</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Alocações Criadas ({resultado.alocacoes.length})</h3>
            </div>
            <div className="card-content">
              <div className="text-sm text-gray-600 mb-4">
                As seguintes alocações foram criadas automaticamente:
              </div>
              
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Caravana</th>
                      <th>Quarto</th>
                      <th>Quantidade</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.alocacoes.map((alocacao, index) => (
                      <tr key={alocacao.id || index}>
                        <td className="font-mono text-sm">#{alocacao.id || 'N/A'}</td>
                        <td>Caravana ID: {alocacao.caravana_id}</td>
                        <td>Quarto ID: {alocacao.quarto_id}</td>
                        <td className="font-semibold">{alocacao.quantidade} pessoas</td>
                        <td>
                          <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Criada
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlocacaoDinamica;